import * as firebaseStore from '../firebase/firestore';
import { httpRequest, isBackendApiEnabled } from './http-client';

const toEpochMs = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'number') return value;
    const timestamp = new Date(value).getTime();
    return Number.isNaN(timestamp) ? null : timestamp;
};

const fallback = (name) => firebaseStore[name];
const TRACKER_DEFAULT_SET_ID = 'set12';
const TRACKER_ENDPOINT_CACHE_TTL_MS = 20000;
const EXPANSION_SET_CACHE_TTL_MS = TRACKER_ENDPOINT_CACHE_TTL_MS;

export const normalizeTrackerSetId = (value) => {
    const raw = String(value || '').trim().toLowerCase();
    if (!raw) return null;
    if (/^\d+$/.test(raw)) {
        return `set${Number.parseInt(raw, 10)}`;
    }
    const setMatch = raw.match(/^set0*(\d+)$/);
    if (setMatch) {
        return `set${Number.parseInt(setMatch[1], 10)}`;
    }
    return raw;
};

export const getGenericTrackerSetName = (setId) => {
    const normalized = normalizeTrackerSetId(setId);
    const match = normalized?.match(/^set(\d+)$/);
    return match ? `Set ${Number.parseInt(match[1], 10)}` : null;
};

const isFalseLike = (value) =>
    value === false ||
    value === 0 ||
    String(value).trim().toLowerCase() === 'false' ||
    String(value).trim() === '0';

const isTrueLike = (value) =>
    value === true ||
    value === 1 ||
    String(value).trim().toLowerCase() === 'true' ||
    String(value).trim() === '1';

export const isTrackerSetActive = (set) =>
    set?.type === 'EXPANSION' && !isFalseLike(set?.is_active) && isTrueLike(set?.tracker_active);

export const getPreferredTrackerSetId = (sets = [], currentSetId = null) => {
    const activeSets = Array.isArray(sets) ? sets : [];
    const normalizedCurrent = normalizeTrackerSetId(currentSetId);
    if (normalizedCurrent && activeSets.some((setItem) => normalizeTrackerSetId(setItem.id) === normalizedCurrent)) {
        return normalizedCurrent;
    }

    const defaultSet = activeSets.find((setItem) => normalizeTrackerSetId(setItem.id) === TRACKER_DEFAULT_SET_ID);
    return normalizeTrackerSetId(defaultSet?.id || activeSets[activeSets.length - 1]?.id || TRACKER_DEFAULT_SET_ID);
};

const mapDeckWithArchetype = (deck, archetypeMap) => ({
    ...deck,
    setId: normalizeTrackerSetId(deck.setId) || deck.setId,
    archetypeInfo: archetypeMap[deck.archetype] || null
});

const ensureTrackerApiEnabled = (operationName) => {
    if (!isBackendApiEnabled()) {
        throw new Error(`Tracker requires backend API (${operationName}).`);
    }
};
const buildDeckContextQuery = (filters = {}) => {
    const query = {};
    if (filters?.format) {
        query.format = filters.format;
    }
    if (filters?.setId) {
        query.setId = normalizeTrackerSetId(filters.setId);
    }
    return query;
};
const hasDeckContextFilters = (filters = {}) => Boolean(filters?.format || filters?.setId);

const withDeckContextFallback = async (primaryRequest, fallbackRequest, filters = {}) => {
    try {
        return await primaryRequest();
    } catch (error) {
        if (!hasDeckContextFilters(filters)) {
            throw error;
        }
        console.warn('Request with deck context failed, retrying without filters.', error);
        return fallbackRequest();
    }
};

export const getUserData = async (userId) => {
    if (isBackendApiEnabled()) return httpRequest('/v1/tracker/me', { cache: false });
    return fallback('getUserData')(userId);
};

export const createOrUpdateUser = async (userId, userData) => {
    if (isBackendApiEnabled()) {
        await httpRequest('/v1/tracker/me', {
            method: 'PATCH',
            body: userData
        });
        return userId;
    }
    return fallback('createOrUpdateUser')(userId, userData);
};

export const updateUserDisplayName = async (userId, displayName) => {
    if (isBackendApiEnabled()) {
        await httpRequest('/v1/tracker/me', {
            method: 'PATCH',
            body: { display_name: displayName }
        });
        return userId;
    }
    return fallback('updateUserDisplayName')(userId, displayName);
};

export const getUserRole = async (userId) => {
    if (isBackendApiEnabled()) {
        const userData = await getUserData(userId);
        return userData?.role || 'user';
    }
    return fallback('getUserRole')(userId);
};

export const isUserAdmin = async (userId) => {
    const role = await getUserRole(userId);
    return role === 'admin' || role === 'superadmin';
};

export const isUserSuperAdmin = async (userId) => {
    const role = await getUserRole(userId);
    return role === 'superadmin';
};

export const isUserPremium = async (userId) => {
    const role = await getUserRole(userId);
    return role === 'premium_user' || role === 'admin' || role === 'superadmin';
};

export const getAllUsers = async () => {
    if (!isBackendApiEnabled()) return fallback('getAllUsers')();
    return httpRequest('/v1/admin/users');
};

export const updateUserRole = async (userId, role) => {
    if (!isBackendApiEnabled()) return fallback('updateUserRole')(userId, role);
    await httpRequest(`/v1/admin/users/${userId}/role`, {
        method: 'PATCH',
        body: { role }
    });
    return userId;
};

export const getUserDecks = async (userId, filters = {}) => {
    ensureTrackerApiEnabled('getUserDecks');
    return withDeckContextFallback(
        () => httpRequest('/v1/tracker/decks', {
            query: { scope: 'mine', ...buildDeckContextQuery(filters) },
            cacheTtlMs: TRACKER_ENDPOINT_CACHE_TTL_MS
        }),
        () => httpRequest('/v1/tracker/decks', {
            query: { scope: 'mine' },
            cacheTtlMs: TRACKER_ENDPOINT_CACHE_TTL_MS
        }),
        filters
    );
};

export const getPublicDecks = async (filters = {}) => {
    ensureTrackerApiEnabled('getPublicDecks');
    return withDeckContextFallback(
        () => httpRequest('/v1/tracker/decks', {
            query: { scope: 'public', ...buildDeckContextQuery(filters) },
            cacheTtlMs: TRACKER_ENDPOINT_CACHE_TTL_MS
        }),
        () => httpRequest('/v1/tracker/decks', {
            query: { scope: 'public' },
            cacheTtlMs: TRACKER_ENDPOINT_CACHE_TTL_MS
        }),
        filters
    );
};

export const getInternalDecks = async (filters = {}) => {
    ensureTrackerApiEnabled('getInternalDecks');
    return withDeckContextFallback(
        () => httpRequest('/v1/tracker/decks', {
            query: { scope: 'internal', ...buildDeckContextQuery(filters) },
            cacheTtlMs: TRACKER_ENDPOINT_CACHE_TTL_MS
        }),
        () => httpRequest('/v1/tracker/decks', {
            query: { scope: 'internal' },
            cacheTtlMs: TRACKER_ENDPOINT_CACHE_TTL_MS
        }),
        filters
    );
};

export const getDeck = async (deckId) => {
    ensureTrackerApiEnabled('getDeck');
    return httpRequest(`/v1/tracker/decks/${deckId}`, { cacheTtlMs: TRACKER_ENDPOINT_CACHE_TTL_MS });
};

export const getDeckById = async (deckId) => {
    ensureTrackerApiEnabled('getDeckById');
    const deck = await getDeck(deckId);
    if (!deck) return null;
    const archetypes = await getAllArchetypes({
        format: deck.format,
        setId: deck.setId
    });
    const archetypeMap = Object.fromEntries(archetypes.map((entry) => [entry.id, entry]));
    return {
        ...deck,
        archetype: archetypeMap[deck.archetype] || deck.archetype
    };
};

export const createDeck = async (deckData) => {
    ensureTrackerApiEnabled('createDeck');
    const created = await httpRequest('/v1/tracker/decks', {
        method: 'POST',
        body: deckData
    });
    return created.id;
};

export const updateDeck = async (deckId, deckData) => {
    ensureTrackerApiEnabled('updateDeck');
    await httpRequest(`/v1/tracker/decks/${deckId}`, {
        method: 'PATCH',
        body: deckData
    });
    return deckId;
};

export const deleteDeck = async (deckId) => {
    ensureTrackerApiEnabled('deleteDeck');
    await httpRequest(`/v1/tracker/decks/${deckId}`, { method: 'DELETE' });
    return true;
};

export const suggestArchetypeForDeck = async ({ cards, format, setId, deckId }) => {
    ensureTrackerApiEnabled('suggestArchetypeForDeck');
    return httpRequest('/v1/tracker/decks/archetype-suggestions', {
        method: 'POST',
        body: { cards, format, setId, deckId }
    });
};

export const saveArchetypeSuggestionFeedback = async (deckId, feedback) => {
    ensureTrackerApiEnabled('saveArchetypeSuggestionFeedback');
    return httpRequest(`/v1/tracker/decks/${deckId}/archetype-suggestion-feedback`, {
        method: 'POST',
        body: feedback
    });
};

export const getAllArchetypes = async (filters = {}) => {
    ensureTrackerApiEnabled('getAllArchetypes');
    return httpRequest('/v1/tracker/archetypes', {
        query: buildDeckContextQuery(filters),
        cacheTtlMs: TRACKER_ENDPOINT_CACHE_TTL_MS
    });
};

export const mergeArchetypes = async ({ sourceArchetypeId, targetArchetypeId, reason }) => {
    ensureTrackerApiEnabled('mergeArchetypes');
    return httpRequest('/v1/tracker/archetypes/merge', {
        method: 'POST',
        body: {
            sourceArchetypeId,
            targetArchetypeId,
            reason: reason || null
        }
    });
};

export const copyArchetypesToScope = async ({
    sourceFormat,
    sourceSetId,
    targetFormat,
    targetSetId,
    overwrite = false
}) => {
    ensureTrackerApiEnabled('copyArchetypesToScope');
    return httpRequest('/v1/tracker/archetypes/copy-scope', {
        method: 'POST',
        body: {
            sourceFormat,
            sourceSetId,
            targetFormat,
            targetSetId,
            overwrite
        }
    });
};

export const assignLegacyArchetypesScope = async ({ targetFormat, targetSetId }) => {
    ensureTrackerApiEnabled('assignLegacyArchetypesScope');
    return httpRequest('/v1/tracker/archetypes/assign-legacy-scope', {
        method: 'POST',
        body: {
            targetFormat,
            targetSetId
        }
    });
};

export const getArchetype = async (archetypeId, filters = {}) => {
    ensureTrackerApiEnabled('getArchetype');
    const archetypes = await getAllArchetypes(filters);
    return archetypes.find((entry) => entry.id === archetypeId) || null;
};

export const createArchetype = async (archetypeData) => {
    ensureTrackerApiEnabled('createArchetype');
    const created = await httpRequest('/v1/tracker/archetypes', {
        method: 'POST',
        body: archetypeData
    });
    return created.id;
};

export const updateArchetype = async (archetypeId, archetypeData) => {
    ensureTrackerApiEnabled('updateArchetype');
    await httpRequest(`/v1/tracker/archetypes/${archetypeId}`, {
        method: 'PATCH',
        body: archetypeData
    });
    return archetypeId;
};

export const deleteArchetype = async (archetypeId) => {
    ensureTrackerApiEnabled('deleteArchetype');
    await httpRequest(`/v1/tracker/archetypes/${archetypeId}`, { method: 'DELETE' });
    return true;
};

export const getDecksWithArchetypes = async (userId = null, filters = {}) => {
    ensureTrackerApiEnabled('getDecksWithArchetypes');
    const [decks, archetypes] = await Promise.all([
        userId ? getUserDecks(userId, filters) : getInternalDecks(filters),
        getAllArchetypes(filters)
    ]);
    const archetypeMap = Object.fromEntries(archetypes.map((entry) => [entry.id, entry]));
    return decks.map((deck) => mapDeckWithArchetype(deck, archetypeMap));
};

export const getDecksByArchetype = async (archetypeId, userId = null, filters = {}) => {
    ensureTrackerApiEnabled('getDecksByArchetype');
    const decks = await getDecksWithArchetypes(userId, filters);
    return decks.filter((deck) => deck.archetype === archetypeId);
};

export const createMatch = async (matchData) => {
    ensureTrackerApiEnabled('createMatch');
    const payload = {
        ...matchData,
        timestamp: toEpochMs(matchData.timestamp)
    };
    const created = await httpRequest('/v1/tracker/matches', {
        method: 'POST',
        body: payload
    });
    return created.id;
};

export const getUserMatches = async (userId) => {
    ensureTrackerApiEnabled('getUserMatches');
    const matches = await httpRequest('/v1/tracker/matches', { cacheTtlMs: TRACKER_ENDPOINT_CACHE_TTL_MS });
    return matches.sort((a, b) => (b.timestamp || 0) - (a.timestamp || 0));
};

export const updateMatch = async (matchId, matchData) => {
    ensureTrackerApiEnabled('updateMatch');
    const payload = {
        ...matchData,
        timestamp: toEpochMs(matchData.timestamp)
    };
    await httpRequest(`/v1/tracker/matches/${matchId}`, {
        method: 'PATCH',
        body: payload
    });
    return true;
};

export const deleteMatch = async (matchId) => {
    ensureTrackerApiEnabled('deleteMatch');
    await httpRequest(`/v1/tracker/matches/${matchId}`, { method: 'DELETE' });
    return true;
};

export const deleteMatches = async (matchIds) => {
    ensureTrackerApiEnabled('deleteMatches');
    await Promise.all(matchIds.map((id) => deleteMatch(id)));
    return true;
};

export const getUserMatchesWithDetails = async (userId, filters = {}) => {
    ensureTrackerApiEnabled('getUserMatchesWithDetails');
    const [matches, userDecks, internalDecks, archetypes] = await Promise.all([
        getUserMatches(),
        getUserDecks(userId, filters),
        getInternalDecks(filters),
        getAllArchetypes(filters)
    ]);
    const userDeckIds = new Set(userDecks.map((deck) => deck.id));
    const decks = [...userDecks];
    internalDecks.forEach((deck) => {
        if (!userDeckIds.has(deck.id)) decks.push(deck);
    });
    const deckMap = Object.fromEntries(decks.map((entry) => [entry.id, entry]));
    const archetypeMap = Object.fromEntries(archetypes.map((entry) => [entry.id, entry]));
    const withDetails = matches.map((match) => {
        const rawDeck = deckMap[match.myDeckId] || null;
        const rawArchetype = rawDeck?.archetype;
        const deckArchetypeId = typeof rawArchetype === 'string' ? rawArchetype : rawArchetype?.id || null;
        const deckArchetypeInfo = deckArchetypeId ? archetypeMap[deckArchetypeId] || null : null;

        return {
            ...match,
            myDeckInfo: rawDeck
                ? {
                    ...rawDeck,
                    archetypeInfo: deckArchetypeInfo
                }
                : null,
            opponentArchetypeInfo: archetypeMap[match.opponentArchetypeId] || null
        };
    });
    if (!filters?.format && !filters?.setId) {
        return withDetails;
    }
    return withDetails.filter((match) => {
        if (!match.myDeckInfo) return false;
        if (filters?.format && match.myDeckInfo.format !== filters.format) return false;
        if (filters?.setId && normalizeTrackerSetId(match.myDeckInfo.setId) !== normalizeTrackerSetId(filters.setId)) return false;
        return true;
    });
};

export const getTrackerDashboardData = async (userId, filters = {}) => {
    ensureTrackerApiEnabled('getTrackerDashboardData');
    const [matches, archetypes, userDecks, internalDecks] = await Promise.all([
        getUserMatchesWithDetails(userId, filters),
        getAllArchetypes(filters),
        getDecksWithArchetypes(userId, filters),
        getDecksWithArchetypes(null, filters)
    ]);

    const allDecks = [...userDecks];
    const userDeckIds = new Set(userDecks.map((deck) => deck.id));
    internalDecks.forEach((deck) => {
        if (!userDeckIds.has(deck.id)) {
            allDecks.push(deck);
        }
    });

    return {
        matches,
        archetypes,
        allDecks
    };
};

export const updateUserLastSelectedDeck = async (userId, deckId) => {
    if (!isBackendApiEnabled()) return fallback('updateUserLastSelectedDeck')(userId, deckId);
    await createOrUpdateUser(userId, { lastSelectedDeckId: deckId });
    return userId;
};

export const updateUserLastSelectedArchetype = async (userId, archetypeId) => {
    if (!isBackendApiEnabled()) return fallback('updateUserLastSelectedArchetype')(userId, archetypeId);
    await createOrUpdateUser(userId, { lastSelectedArchetypeId: archetypeId });
    return userId;
};

export const updateUserTrackerContext = async (userId, context = {}) => {
    if (!isBackendApiEnabled()) return userId;
    const payload = {};
    if (typeof context.format === 'string' && context.format.trim()) {
        payload.trackerSelectedFormat = context.format.trim();
    }
    if (typeof context.setId === 'string' && context.setId.trim()) {
        payload.trackerSelectedSetId = normalizeTrackerSetId(context.setId) || context.setId.trim();
    }
    if (!Object.keys(payload).length) return userId;
    await createOrUpdateUser(userId, payload);
    return userId;
};

export const getAggregatedArchetypeStats = async (userId, filters = {}) => {
    ensureTrackerApiEnabled('getAggregatedArchetypeStats');
    return withDeckContextFallback(
        async () => {
            const response = await httpRequest('/v1/tracker/stats/archetypes/aggregated', {
                query: buildDeckContextQuery(filters),
                cacheTtlMs: TRACKER_ENDPOINT_CACHE_TTL_MS
            });
            return response?.stats || response || {};
        },
        async () => {
            const response = await httpRequest('/v1/tracker/stats/archetypes/aggregated', { cacheTtlMs: TRACKER_ENDPOINT_CACHE_TTL_MS });
            return response?.stats || response || {};
        },
        filters
    );
};

export const getAggregatedArchetypeStatsSnapshot = async (filters = {}) => {
    ensureTrackerApiEnabled('getAggregatedArchetypeStatsSnapshot');
    return withDeckContextFallback(
        () => httpRequest('/v1/tracker/stats/archetypes/aggregated', {
            query: buildDeckContextQuery(filters),
            cacheTtlMs: TRACKER_ENDPOINT_CACHE_TTL_MS
        }),
        () => httpRequest('/v1/tracker/stats/archetypes/aggregated', { cacheTtlMs: TRACKER_ENDPOINT_CACHE_TTL_MS }),
        filters
    );
};

export const refreshAggregatedArchetypeStats = async (filters = {}) => {
    ensureTrackerApiEnabled('refreshAggregatedArchetypeStats');
    return withDeckContextFallback(
        () => httpRequest('/v1/tracker/stats/archetypes/aggregated/refresh', {
            method: 'POST',
            query: buildDeckContextQuery(filters)
        }),
        () => httpRequest('/v1/tracker/stats/archetypes/aggregated/refresh', {
            method: 'POST'
        }),
        filters
    );
};

export const getMatchupBreakdown = async (archetypeId, opponentArchetypeId, filters = {}) => {
    ensureTrackerApiEnabled('getMatchupBreakdown');
    const baseQuery = { archetypeId, opponentArchetypeId };
    return withDeckContextFallback(
        () => httpRequest('/v1/tracker/stats/matchup/players', {
            query: { ...baseQuery, ...buildDeckContextQuery(filters) }
        }),
        () => httpRequest('/v1/tracker/stats/matchup/players', { query: baseQuery }),
        filters
    );
};

export const getArchetypeRankings = async (filters = {}) => {
    ensureTrackerApiEnabled('getArchetypeRankings');
    return withDeckContextFallback(
        () => httpRequest('/v1/tracker/archetype-rankings', {
            query: buildDeckContextQuery(filters),
            cacheTtlMs: TRACKER_ENDPOINT_CACHE_TTL_MS
        }),
        () => httpRequest('/v1/tracker/archetype-rankings', { cacheTtlMs: TRACKER_ENDPOINT_CACHE_TTL_MS }),
        filters
    );
};

export const setArchetypeRanking = async (archetypeId, ranking, filters = {}) => {
    ensureTrackerApiEnabled('setArchetypeRanking');
    return withDeckContextFallback(
        () => httpRequest(`/v1/tracker/archetype-rankings/${archetypeId}`, {
            method: 'PUT',
            query: buildDeckContextQuery(filters),
            body: { ranking }
        }),
        () => httpRequest(`/v1/tracker/archetype-rankings/${archetypeId}`, {
            method: 'PUT',
            body: { ranking }
        }),
        filters
    );
};

export const getAllSets = async () => {
    if (!isBackendApiEnabled()) return fallback('getAllSets')();
    return httpRequest('/v1/limited/sets', { query: { includeInactive: true } });
};

export const updateSet = async (setId, updateData) => {
    if (!isBackendApiEnabled()) return fallback('updateSet')(setId, updateData);
    await httpRequest(`/v1/limited/sets/${setId}`, {
        method: 'PATCH',
        body: updateData
    });
};

export const deleteSet = async (setId) => {
    if (!isBackendApiEnabled()) {
        throw new Error('Deleting sets requires backend API.');
    }
    await httpRequest(`/v1/limited/sets/${setId}`, {
        method: 'DELETE'
    });
};

export const getExpansionSets = async () => {
    if (!isBackendApiEnabled()) return fallback('getExpansionSets')();
    const sets = await httpRequest('/v1/limited/sets', {
        query: { includeInactive: true },
        cacheTtlMs: EXPANSION_SET_CACHE_TTL_MS
    });
    return sets
        .filter(isTrackerSetActive)
        .map((entry) => {
            return {
                ...entry,
                name: getGenericTrackerSetName(entry.id) || entry.name || entry.id
            };
        })
        .sort((a, b) => (a.sort_number || 0) - (b.sort_number || 0));
};

export { TRACKER_DEFAULT_SET_ID };

export const getAllExpansionSetsForAdmin = async () => {
    if (!isBackendApiEnabled()) return fallback('getAllExpansionSetsForAdmin')();
    const sets = await httpRequest('/v1/limited/sets', { query: { includeInactive: true } });
    return sets.filter((entry) => entry.type === 'EXPANSION');
};

export const getAllLimitedCards = async () => {
    if (!isBackendApiEnabled()) return fallback('getAllLimitedCards')();
    return httpRequest('/v1/limited/cards');
};

export const getLimitedCard = async (cardId) => {
    if (!isBackendApiEnabled()) return fallback('getLimitedCard')(cardId);
    return httpRequest(`/v1/limited/cards/${cardId}`);
};

export const updateLimitedCardRating = async (cardId, ratingData) => {
    if (!isBackendApiEnabled()) return fallback('updateLimitedCardRating')(cardId, ratingData);
    await httpRequest(`/v1/limited/cards/${cardId}`, {
        method: 'PATCH',
        body: ratingData
    });
    return cardId;
};

export const updateLimitedCard = async (cardId, updateData) => {
    if (!isBackendApiEnabled()) return fallback('updateLimitedCard')(cardId, updateData);
    await httpRequest(`/v1/limited/cards/${cardId}`, {
        method: 'PATCH',
        body: updateData
    });
    return cardId;
};

const decorateLimitedCardsWithSets = async (includeInactiveSets) => {
    const [cards, sets] = await Promise.all([
        getAllLimitedCards(),
        httpRequest('/v1/limited/sets', { query: { includeInactive: includeInactiveSets } })
    ]);
    const activeExpansionSets = sets.filter((set) => set.type === 'EXPANSION' && (includeInactiveSets || set.is_active !== false));
    const allSetsMap = Object.fromEntries(sets.map((set) => [set.id, set]));
    const expansionSetMap = Object.fromEntries(activeExpansionSets.map((set) => [set.id, set]));

    return cards
        .filter((card) => {
            if (card.rarity === 'ENCHANTED' || card.rarity === 'SPECIAL') return false;
            if (card.card_identifier) {
                const [first, second] = card.card_identifier.split('/');
                const firstNumber = Number.parseInt(first, 10);
                const secondNumber = Number.parseInt(second, 10);
                if (!Number.isNaN(firstNumber) && !Number.isNaN(secondNumber) && firstNumber > secondNumber) {
                    return false;
                }
            }
            if (!Array.isArray(card.card_sets) || card.card_sets.length === 0) return false;
            return card.card_sets.some((setId) => expansionSetMap[setId]);
        })
        .map((card) => {
            const firstExpansionId = card.card_sets.find((setId) => expansionSetMap[setId]);
            const sortedSets = [...card.card_sets].sort((a, b) => {
                const sortA = allSetsMap[a]?.sort_number || 0;
                const sortB = allSetsMap[b]?.sort_number || 0;
                return sortA - sortB;
            });
            return {
                ...card,
                set: expansionSetMap[firstExpansionId] || null,
                card_sets: sortedSets
            };
        })
        .sort((a, b) => (a.sort_number || 0) - (b.sort_number || 0));
};

export const getLimitedCardsWithSets = async () => {
    if (!isBackendApiEnabled()) return fallback('getLimitedCardsWithSets')();
    return decorateLimitedCardsWithSets(false);
};

export const getAllLimitedCardsForAdmin = async () => {
    if (!isBackendApiEnabled()) return fallback('getAllLimitedCardsForAdmin')();
    return getAllLimitedCards();
};

export const getAllLimitedCardsForAdminWithSets = async () => {
    if (!isBackendApiEnabled()) return fallback('getAllLimitedCardsForAdminWithSets')();
    return decorateLimitedCardsWithSets(true);
};

export const getArchetypeGuide = async (archetypeId) => {
    if (!isBackendApiEnabled()) return { guide: null };
    return httpRequest(`/v1/tracker/archetypes/${encodeURIComponent(archetypeId)}/guide`, { cache: false });
};

export const saveArchetypeGuide = async (archetypeId, content) => {
    if (!isBackendApiEnabled()) throw new Error('Backend API not enabled');
    return httpRequest(`/v1/tracker/archetypes/${encodeURIComponent(archetypeId)}/guide`, {
        method: 'PUT',
        body: { content }
    });
};

export const checkCanEditArchetypeGuide = async (archetypeId) => {
    if (!isBackendApiEnabled()) return false;
    try {
        const result = await httpRequest(`/v1/tracker/archetypes/${encodeURIComponent(archetypeId)}/can-edit-guide`, { cache: false });
        return Boolean(result?.canEdit);
    } catch {
        return false;
    }
};

export const getArchetypeGuideEditors = async (archetypeId) => {
    if (!isBackendApiEnabled()) return [];
    return httpRequest(`/v1/admin/archetypes/${encodeURIComponent(archetypeId)}/guide-editors`, { cache: false });
};

export const addArchetypeGuideEditor = async (archetypeId, userUid) => {
    if (!isBackendApiEnabled()) throw new Error('Backend API not enabled');
    return httpRequest(`/v1/admin/archetypes/${encodeURIComponent(archetypeId)}/guide-editors`, {
        method: 'POST',
        body: { user_uid: userUid }
    });
};

export const removeArchetypeGuideEditor = async (archetypeId, userUid) => {
    if (!isBackendApiEnabled()) throw new Error('Backend API not enabled');
    return httpRequest(`/v1/admin/archetypes/${encodeURIComponent(archetypeId)}/guide-editors/${encodeURIComponent(userUid)}`, {
        method: 'DELETE'
    });
};

export const migrateGuideEditorsTable = async () => {
    if (!isBackendApiEnabled()) throw new Error('Backend API not enabled');
    return httpRequest('/v1/admin/migrate/guide-editors', { method: 'POST' });
};

// Non-migrated helpers (still served from Firebase for now).
export const uploadDeckImage = firebaseStore.uploadDeckImage;
export const deleteDeckImage = firebaseStore.deleteDeckImage;
export const getDeckImageURL = firebaseStore.getDeckImageURL;
